/* @img-migrated */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Search, Calendar, ArrowRight, ExternalLink,
  Clock, BookOpen, Globe, Building2, Briefcase, Landmark, Palmtree, Megaphone, Sparkles,
  ChevronLeft, ChevronRight, Plus,
} from 'lucide-react';
import LeadershipSignature from '../components/LeadershipSignature';
import { Input } from '../components/ui/input';
import { NewsAdSlot } from '../components/NewsAdSlot';

import Img from '../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'All', icon: Globe },
  { id: 'event_recap', label: 'Event Recaps', icon: Sparkles },
  { id: 'economy', label: 'Economy', icon: Landmark },
  { id: 'business', label: 'Business', icon: Briefcase },
  { id: 'investment', label: 'Investment', icon: TrendingUp },
  { id: 'trade', label: 'Trade', icon: Building2 },
  { id: 'entrepreneurship', label: 'Entrepreneurship', icon: BookOpen },
  { id: 'tourism', label: 'Tourism', icon: Palmtree },
  { id: 'chamber', label: 'Chamber Updates', icon: Megaphone },
];

const NEWS_PLACEHOLDER = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=675&fit=crop&auto=format&q=80';

// Use the canonical helper; only override for the empty/null case (NewsPage
// wants a generic news photo when no image is provided, NOT a DCCF logo).
import { resolveImg as _resolveImg } from '../lib/images';
const resolveImg = (u) => _resolveImg(u) || NEWS_PLACEHOLDER;

const sourceColors = {
  'Listín Diario · Economía': 'bg-blue-50 text-blue-700 border-blue-200',
  'Diario Libre · Economía': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Hoy': 'bg-orange-50 text-orange-700 border-orange-200',
  'DCCF Blog': 'bg-[#E63946]/10 text-[#E63946] border-[#E63946]/20',
};

const categoryColors = {
  economy: 'bg-blue-100 text-blue-700',
  business: 'bg-slate-100 text-slate-700',
  investment: 'bg-green-100 text-green-700',
  trade: 'bg-purple-100 text-purple-700',
  entrepreneurship: 'bg-amber-100 text-amber-700',
  tourism: 'bg-cyan-100 text-cyan-700',
  chamber: 'bg-[#E63946]/10 text-[#E63946]',
  event_recap: 'bg-amber-100 text-amber-700',
};

const NewsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [externalNews, setExternalNews] = useState([]);
  const [dollarRate, setDollarRate] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogsRes, rateRes, extRes] = await Promise.all([
          axios.get(`${API}/blog?published=true`).catch(() => ({ data: [] })),
          axios.get(`${API}/dollar-rate`).catch(() => ({ data: null })),
          axios.get(`${API}/external-news?limit=30`).catch(() => ({ data: { items: [] } })),
        ]);
        setBlogs(blogsRes.data);
        setDollarRate(rateRes.data);
        setExternalNews(extRes.data?.items || []);
      } catch { /* */ } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const chamberBlogs = blogs.map(b => ({
    ...b, source: 'DCCF Blog', category: b.category || 'chamber',
    is_external: false, excerpt: b.excerpt || (b.content || '').replace(/<[^>]+>/g,'').slice(0, 150) + '…',
    image: b.image_url || b.featured_image || NEWS_PLACEHOLDER,
    image_position: b.image_position || 'center',
    date: (b.published_at || b.created_at || '').split('T')[0],
    source_event_id: b.source_event_id,
    gallery_images: b.gallery_images || [],
  }));

  // Map live RSS items into the unified card shape used by the rest of the page.
  const externalCards = externalNews.map((n) => ({
    id: n.id,
    title: n.title,
    excerpt: n.summary || '',
    image: n.image_url || NEWS_PLACEHOLDER,
    category: n.category || 'economy',
    source: n.source_label,
    date: (n.published_at || '').split('T')[0],
    is_external: true,
    featured: !!n.featured,
    url: n.link,
  }));

  const allContent = [...chamberBlogs, ...externalCards].sort((a, b) => {
    // Featured external news bubbles up; everything else is by date.
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return (b.date || '').localeCompare(a.date || '');
  });

  const filtered = allContent.filter(item => {
    if (activeCategory !== 'all' && item.category !== activeCategory) return false;
    if (search) {
      const s = search.toLowerCase();
      return item.title?.toLowerCase().includes(s) || item.excerpt?.toLowerCase().includes(s);
    }
    return true;
  });

  // Hierarchy:
  //   • featured[0]      → main hero card (1 big)
  //   • featured[1..2]   → 2 secondary hero cards
  //   • carouselPool     → next 6-10 items, swipeable autoplay strip
  //   • feed             → image-left / text-right cards in chunks of 6
  const featured = useMemo(() => allContent.slice(0, 3), [allContent]);
  const carouselPool = useMemo(() => allContent.slice(3, 12), [allContent]);
  // The "Latest" sidebar list pulls from the *unfiltered* pool so it stays steady
  // when the user changes category or searches.
  const latestForSidebar = useMemo(() => allContent.slice(0, 8), [allContent]);
  const heroIds = useMemo(() => new Set(featured.map((f) => f.id)), [featured]);
  // Filtered, hero-excluded feed (image-left / text-right cards).
  const feed = useMemo(
    () => filtered.filter((it) => !heroIds.has(it.id)),
    [filtered, heroIds],
  );

  const PAGE_SIZE = 6;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Reset paging when filters change.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, search]);

  return (
    <div className="bg-[#F8F9FB] min-h-screen" data-testid="news-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#1B2838] to-[#002249]" />
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M0 40L40 0H20L0 20M40 40V20L20 40\'/%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-24 sm:pt-28 pb-10 sm:pb-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[#F4A261] text-xs sm:text-sm font-semibold tracking-wider uppercase mb-2.5">DCCF Intelligence</p>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
              News & Insights
            </h1>
            <p className="text-white/55 text-sm sm:text-base lg:text-lg max-w-xl leading-relaxed">
              Business, economy, and opportunities connecting Florida and the Dominican Republic.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== EXCHANGE RATE BAR — Premium Redesign ===== */}
      {dollarRate && (
        <motion.section
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="bg-white border-b border-slate-200/80 sticky top-16 sm:top-[72px] md:top-20 z-30"
          data-testid="dollar-bar"
        >
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <div className="flex items-center justify-between py-3 sm:py-3.5 gap-4">
              {/* Left: Currency pair + rate + change */}
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                {/* Flags + pair label */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center -space-x-1">
                    <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full overflow-hidden border-2 border-white shadow-sm inline-flex items-center justify-center bg-[#002868]" title="United States">
                      <svg viewBox="0 0 32 32" className="w-full h-full"><rect fill="#B22234" width="32" height="32"/><rect fill="#fff" y="2.46" width="32" height="2.46"/><rect fill="#fff" y="7.38" width="32" height="2.46"/><rect fill="#fff" y="12.31" width="32" height="2.46"/><rect fill="#fff" y="17.23" width="32" height="2.46"/><rect fill="#fff" y="22.15" width="32" height="2.46"/><rect fill="#fff" y="27.08" width="32" height="2.46"/><rect fill="#002868" width="12.8" height="17.23"/></svg>
                    </span>
                    <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full overflow-hidden border-2 border-white shadow-sm inline-flex items-center justify-center" title="Dominican Republic">
                      <svg viewBox="0 0 32 32" className="w-full h-full"><rect fill="#002D62" width="16" height="16"/><rect fill="#CE1126" x="16" width="16" height="16"/><rect fill="#CE1126" width="16" y="16" height="16"/><rect fill="#002D62" x="16" y="16" width="16" height="16"/><rect fill="#fff" x="13" width="6" height="32"/><rect fill="#fff" y="13" width="32" height="6"/></svg>
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-widest leading-none">USD / DOP</span>
                    <span className="text-[10px] text-slate-300 leading-none mt-0.5 hidden sm:block">Dollar to Peso</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-8 w-px bg-slate-200 flex-shrink-0 hidden xs:block" />

                {/* Rate value */}
                <div className="flex items-baseline gap-2 flex-shrink-0">
                  <span className="text-xl sm:text-2xl font-bold text-[#0D1B2A] tabular-nums tracking-tight">{dollarRate.buy?.toFixed(2)}</span>
                  <span className={`inline-flex items-center gap-0.5 text-xs sm:text-sm font-semibold ${dollarRate.direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {dollarRate.direction === 'up' ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {dollarRate.direction === 'up' ? '+' : '-'}{Math.abs(dollarRate.change || 0).toFixed(2)}
                  </span>
                </div>

                {/* Buy/Sell */}
                <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500 flex-shrink-0">
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">Buy</span>
                    <span className="font-semibold text-slate-700 tabular-nums">{dollarRate.buy?.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">Sell</span>
                    <span className="font-semibold text-slate-700 tabular-nums">{dollarRate.sell?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Right: LIVE badge + source */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="hidden md:flex flex-col items-end">
                  <span className="text-[10px] text-slate-400 leading-tight">Official reference</span>
                  <span className="text-[10px] text-slate-500 font-medium leading-tight">Banco Central de la Rep. Dominicana</span>
                </div>
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wide" data-testid="live-badge">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  Live
                </span>
              </div>
            </div>
            {/* Mobile source line */}
            <div className="block md:hidden pb-2.5 -mt-1">
              <span className="text-[10px] text-slate-400">Tasa de cambio &middot; Referencia oficial del Banco Central RD</span>
            </div>
          </div>
        </motion.section>
      )}

      {/* ===== HERO STORIES — 1 dominant + 2 secondary, minimal overlay ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-10 pb-4 sm:pb-6" data-testid="featured-news">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          {featured[0] && <HeroMainCard item={featured[0]} categoryColors={categoryColors} resolveImg={resolveImg} placeholder={NEWS_PLACEHOLDER} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-5">
            {featured.slice(1, 3).map((item, i) => (
              <HeroSideCard key={item.id} item={item} index={i} categoryColors={categoryColors} resolveImg={resolveImg} placeholder={NEWS_PLACEHOLDER} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== TOP STORIES CAROUSEL — autoplay, swipeable, no text overlays ===== */}
      {carouselPool.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-8 sm:pb-10" data-testid="top-stories-carousel">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[12px] sm:text-[13px] font-bold tracking-[0.2em] uppercase text-slate-500">Top Stories</h2>
            <span className="text-[10.5px] text-slate-400 font-medium hidden sm:inline">Auto-rotating</span>
          </div>
          <TopStoriesCarousel items={carouselPool} categoryColors={categoryColors} resolveImg={resolveImg} placeholder={NEWS_PLACEHOLDER} />
        </section>
      )}

      {/* ===== SEARCH + FILTERS ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 mb-6 sm:mb-8" data-testid="search-filters">
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search business topics..."
                className="pl-10 h-10 rounded-lg border-slate-200 focus:border-[#002249] focus:ring-[#002249]/10"
                data-testid="news-search"
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 overflow-x-auto pb-1 -mb-1" data-testid="category-filters">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-[#0D1B2A] text-white shadow-sm'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                <cat.icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MAIN FEED (image-left / text-right) + SIDEBAR ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16 sm:pb-20" data-testid="news-feed">
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {feed.length > 0 ? feed.slice(0, visibleCount).map((item, index) => (
              <React.Fragment key={item.id}>
                <FeedCard item={item} index={index} categoryColors={categoryColors} resolveImg={resolveImg} placeholder={NEWS_PLACEHOLDER} />
                {(index + 1) % 4 === 0 && index < Math.min(visibleCount, feed.length) - 1 && (
                  <NewsAdSlot placement="mid" />
                )}
              </React.Fragment>
            )) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No articles found</p>
                <p className="text-sm text-slate-400 mt-1">Try a different search term or category.</p>
              </div>
            )}

            {/* Load More */}
            {feed.length > visibleCount && (
              <div className="pt-2 sm:pt-4 text-center" data-testid="load-more-wrapper">
                <button
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold bg-white border border-slate-200 text-[#002249] hover:bg-[#002249] hover:text-white hover:border-[#002249] transition-colors shadow-sm"
                  data-testid="load-more-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Load {Math.min(PAGE_SIZE, feed.length - visibleCount)} more
                  <span className="text-[10.5px] opacity-60 font-normal">· {visibleCount} of {feed.length}</span>
                </button>
              </div>
            )}
          </div>

          {/* ===== Sidebar — Latest / Resources / CTA / Ads ===== */}
          <aside className="space-y-4 sm:space-y-5" data-testid="news-sidebar">
            {/* Latest updates — compact list driven from full pool */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5" data-testid="sidebar-latest">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[11.5px] font-bold tracking-[0.18em] uppercase text-slate-500 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Latest updates
                </h3>
              </div>
              {latestForSidebar.length === 0 ? (
                <p className="text-sm text-slate-400">No updates available.</p>
              ) : latestForSidebar.map((b, i) => {
                const Wrap = b.is_external
                  ? ({ children, ...rest }) => <a {...rest} href={b.url || '#'} target="_blank" rel="noopener noreferrer">{children}</a>
                  : ({ children, ...rest }) => <Link {...rest} to={`/news/${b.id}`}>{children}</Link>;
                return (
                  <Wrap key={b.id} className={`block py-2.5 group ${i > 0 ? 'border-t border-slate-100' : ''}`} data-testid={`sidebar-latest-${i}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 truncate">{b.source}</p>
                    <h4 className="text-[13px] font-semibold text-[#0D1B2A] leading-snug group-hover:text-[#E63946] transition-colors line-clamp-2">{b.title}</h4>
                  </Wrap>
                );
              })}
            </div>

            {/* Sponsored Ad — top placement */}
            <NewsAdSlot placement="top" />

            {/* Resources */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5" data-testid="sidebar-resources">
              <h3 className="text-[11.5px] font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Resources</h3>
              <div className="space-y-1">
                {[
                  { label: 'Banco Central RD', url: 'https://www.bancentral.gov.do' },
                  { label: 'Listín Diario', url: 'https://listindiario.com' },
                  { label: 'Diario Libre', url: 'https://www.diariolibre.com' },
                  { label: 'DR Trade & Investment', url: 'https://www.investinthedr.com' },
                ].map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-2 text-[13px] text-slate-600 hover:text-[#002249] transition-colors group border-b border-slate-50 last:border-0">
                    <span>{link.label}</span>
                    <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-[#002249]" />
                  </a>
                ))}
              </div>
            </div>

            {/* Member CTA */}
            <div className="bg-gradient-to-br from-[#0D1B2A] to-[#002249] rounded-xl p-5 text-white" data-testid="sidebar-cta">
              <p className="text-[10.5px] text-[#F4A261] font-semibold uppercase tracking-wider mb-1.5">Stay Connected</p>
              <h3 className="text-[15px] font-bold mb-1.5 leading-snug">Get exclusive business intelligence</h3>
              <p className="text-white/55 text-[12px] mb-3 leading-relaxed">Join the DCCF for member-only insights and opportunities.</p>
              <Link to="/member-login" className="inline-flex items-center px-3.5 py-2 bg-[#E63946] hover:bg-[#D32F3F] rounded-lg text-[12px] font-semibold transition-colors">
                Member Portal <ArrowRight className="w-3 h-3 ml-1.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* ===== BOTTOM SPONSORED SPOTLIGHT ===== */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-10 sm:pb-12">
        <NewsAdSlot placement="bottom" />
      </section>
      {/* Leadership Signature — Communications */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <LeadershipSignature title="Director of Communications" label="News & Media" blurb="For press inquiries, content submissions, or media partnerships, connect with our Director of Communications." />
        </div>
      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Hero & feed cards — extracted for clarity. No heavy text overlays:
// only the category badge + title sit on the image; supporting copy
// (date, source) lives on the card body, not on the photo.
// ─────────────────────────────────────────────────────────────────────

const HeroMainCard = ({ item, categoryColors, resolveImg, placeholder }) => {
  const Wrap = item.is_external
    ? ({ children }) => <a href={item.url || '#'} target="_blank" rel="noopener noreferrer" data-testid="featured-main-link">{children}</a>
    : ({ children }) => <Link to={`/news/${item.id}`} data-testid="featured-main-link">{children}</Link>;
  return (
    <Wrap>
      <motion.article
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="lg:col-span-2 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
        data-testid="featured-main"
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
          <Img variant="generic"
            src={resolveImg(item.image)}
            alt={item.title}
            onError={(e) => { if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder; }}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
            style={{ objectPosition: `center ${item.image_position || 'center'}` }}
          />
          {/* Minimal-only overlay: a single category pill, top-left. No darkening, no excerpt. */}
          <span className={`absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded backdrop-blur-sm ${categoryColors[item.category] || 'bg-white/90 text-slate-700'}`}>
            {(item.category === 'event_recap' ? 'Event Recap' : item.category || 'News').toUpperCase()}
          </span>
        </div>
        <div className="p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{item.source} · {item.date}</p>
          <h2 className="text-[18px] sm:text-2xl font-bold text-[#0D1B2A] leading-snug group-hover:text-[#E63946] transition-colors">{item.title}</h2>
          {item.excerpt && <p className="text-[13.5px] text-slate-600 mt-2 line-clamp-2 leading-relaxed">{item.excerpt}</p>}
        </div>
      </motion.article>
    </Wrap>
  );
};

const HeroSideCard = ({ item, index, categoryColors, resolveImg, placeholder }) => {
  const Wrap = item.is_external
    ? ({ children }) => <a href={item.url || '#'} target="_blank" rel="noopener noreferrer" data-testid={`featured-side-${index}-link`}>{children}</a>
    : ({ children }) => <Link to={`/news/${item.id}`} data-testid={`featured-side-${index}-link`}>{children}</Link>;
  return (
    <Wrap>
      <motion.article
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + index * 0.05, duration: 0.4 }}
        className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
        data-testid={`featured-side-${index}`}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
          <Img variant="generic"
            src={resolveImg(item.image)}
            alt={item.title}
            onError={(e) => { if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder; }}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
            style={{ objectPosition: `center ${item.image_position || 'center'}` }}
          />
          <span className={`absolute top-2.5 left-2.5 text-[9.5px] font-bold px-1.5 py-0.5 rounded ${categoryColors[item.category] || 'bg-white/90 text-slate-700'}`}>
            {(item.category === 'event_recap' ? 'Event Recap' : item.category || 'News').toUpperCase()}
          </span>
        </div>
        <div className="p-3 sm:p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 truncate">{item.source}</p>
          <h3 className="text-[14px] font-bold text-[#0D1B2A] leading-snug group-hover:text-[#E63946] transition-colors line-clamp-3">{item.title}</h3>
        </div>
      </motion.article>
    </Wrap>
  );
};

// Image-left (sm+) / image-top (mobile), text-right card. No overlays at all.
const FeedCard = ({ item, index, categoryColors, resolveImg, placeholder }) => {
  const Wrap = item.is_external
    ? ({ children }) => <a href={item.url || '#'} target="_blank" rel="noopener noreferrer" className="block" data-testid={`news-card-${index}-link`}>{children}</a>
    : ({ children }) => <Link to={`/news/${item.id}`} className="block" data-testid={`news-card-${index}-link`}>{children}</Link>;
  return (
    <Wrap>
      <motion.article
        initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ delay: Math.min(index * 0.04, 0.2) }}
        className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer"
        data-testid={`news-card-${index}`}
      >
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-[200px] aspect-[16/9] sm:aspect-auto sm:h-auto flex-shrink-0 overflow-hidden bg-slate-100 relative">
            <Img variant="generic"
              src={resolveImg(item.image)}
              alt={item.title}
              onError={(e) => { if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder; }}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              style={{ objectPosition: `center ${item.image_position || 'center'}` }}
            />
          </div>
          <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-h-[120px]">
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${categoryColors[item.category] || 'bg-slate-100 text-slate-600'}`}>
                  {(item.category === 'event_recap' ? 'Event Recap' : item.category || 'News').toUpperCase()}
                </span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider truncate">{item.source}</span>
              </div>
              <h3 className="text-[15px] sm:text-[16px] font-bold text-[#0D1B2A] leading-snug group-hover:text-[#E63946] transition-colors">{item.title}</h3>
              {item.excerpt && <p className="text-[13px] text-slate-500 mt-1.5 line-clamp-2 leading-relaxed hidden sm:block">{item.excerpt}</p>}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <span className="text-[11px] text-slate-400 flex items-center"><Calendar className="w-3 h-3 mr-1" />{item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
              <span className="text-[12px] font-semibold text-[#002249] group-hover:text-[#E63946] transition-colors flex items-center">Read more <ArrowRight className="w-3 h-3 ml-1" /></span>
            </div>
          </div>
        </div>
      </motion.article>
    </Wrap>
  );
};

// Top Stories carousel — one slide visible (mobile / md), two on lg+.
// Autoplay 7s with crossfade. Swipeable on touch via Framer drag.
const TopStoriesCarousel = ({ items, categoryColors, resolveImg, placeholder }) => {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  // Group into 2-up "pages" on lg, 1-up otherwise. We use pure CSS for layout
  // and `page` indexes into the leading slide.
  const pages = items.length;
  const timer = useRef(null);
  useEffect(() => {
    if (paused || pages <= 1) return undefined;
    timer.current = setTimeout(() => setPage((p) => (p + 1) % pages), 7000);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [page, paused, pages]);

  const go = (dir) => setPage((p) => (p + dir + pages) % pages);

  return (
    <div
      className="relative bg-white rounded-xl shadow-sm overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="carousel-root"
    >
      <div className="relative aspect-[16/8] sm:aspect-[16/6] overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.div
            key={page}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);
              else if (info.offset.x > 60) go(-1);
            }}
            initial={{ opacity: 0, scale: 1.01 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.01 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            <CarouselSlide item={items[page]} categoryColors={categoryColors} resolveImg={resolveImg} placeholder={placeholder} />
          </motion.div>
        </AnimatePresence>

        {pages > 1 && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous" className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow text-[#002249] items-center justify-center backdrop-blur" data-testid="carousel-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => go(1)} aria-label="Next" className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow text-[#002249] items-center justify-center backdrop-blur" data-testid="carousel-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1.5 py-2.5 bg-white" data-testid="carousel-dots">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === page ? 'bg-[#002249] w-6' : 'bg-slate-300 w-1.5 hover:bg-slate-400'}`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CarouselSlide = ({ item, categoryColors, resolveImg, placeholder }) => {
  if (!item) return null;
  const Wrap = item.is_external
    ? ({ children }) => <a href={item.url || '#'} target="_blank" rel="noopener noreferrer" className="block h-full">{children}</a>
    : ({ children }) => <Link to={`/news/${item.id}`} className="block h-full">{children}</Link>;
  return (
    <Wrap>
      <div className="relative h-full bg-slate-100">
        <Img variant="generic"
          src={resolveImg(item.image)}
          alt={item.title}
          onError={(e) => { if (e.currentTarget.src !== placeholder) e.currentTarget.src = placeholder; }}
          className="w-full h-full object-cover"
          style={{ objectPosition: `center ${item.image_position || 'center'}` }}
        />
        {/* Bottom gradient just enough for legibility — no full text wash */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pt-16 pb-4 px-5 sm:px-7">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${categoryColors[item.category] || 'bg-white/90 text-slate-700'}`}>
              {(item.category === 'event_recap' ? 'Event Recap' : item.category || 'News').toUpperCase()}
            </span>
            <span className="text-[10px] font-semibold text-white/85 uppercase tracking-wider truncate">{item.source}</span>
          </div>
          <h3 className="text-white font-bold leading-tight text-[15px] sm:text-[20px] max-w-3xl line-clamp-2">
            {item.title}
          </h3>
        </div>
      </div>
    </Wrap>
  );
};

export default NewsPage;
